import type { CSSProperties } from 'react';

/**
 * Skeleton con shimmer animation. Más informativo que un spinner: el user
 * ve el shape de lo que viene (filas de tabla, card, etc.) y la espera
 * percibida es menor.
 *
 * Uso:
 *   <Skeleton width="100%" height={48} />          // bloque solo
 *   <Skeleton circle width={32} height={32} />     // avatar/dot
 *   <SkeletonRows count={5} rowHeight={48} />      // filas de tabla
 *
 * Estilos: viven en globals.css (clase .skeleton + keyframes shimmer).
 */
interface SkeletonProps {
  width?: number | string;
  height?: number | string;
  circle?: boolean;
  className?: string;
  style?: CSSProperties;
}

export function Skeleton({ width, height, circle, className, style }: SkeletonProps) {
  const cls = `skeleton${circle ? ' skeleton--circle' : ''}${className ? ` ${className}` : ''}`;
  return (
    <span
      className={cls}
      aria-hidden="true"
      style={{
        display: 'inline-block',
        width: width ?? '100%',
        height: height ?? 14,
        ...style,
      }}
    />
  );
}

/**
 * N filas de skeleton apiladas. Útil para placeholder de listas/tablas.
 * El parent debe tener role/aria-live apropiado para anunciar "Cargando…"
 * sin que el lector lea cada skeleton fila.
 */
interface SkeletonRowsProps {
  count: number;
  rowHeight?: number;
  gap?: number;
  className?: string;
}

export function SkeletonRows({ count, rowHeight = 48, gap = 8, className }: SkeletonRowsProps) {
  return (
    <div
      className={className}
      role="status"
      aria-busy="true"
      aria-label="Cargando"
      style={{ display: 'flex', flexDirection: 'column', gap, width: '100%' }}
    >
      {Array.from({ length: count }, (_, i) => (
        <Skeleton key={i} height={rowHeight} />
      ))}
    </div>
  );
}
